import React, { useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { ClockWatch } from 'shared/assets/icons';
import { TimePickerMenu } from 'shared/components/ui/time-picker-menu';
import { useOpenMenu } from 'shared/hooks/use-open-menu';
import { TimeFormatOptions } from 'shared/types/date-format-options';
import { isNumber } from 'shared/utils/is-number';
import { parseDateTime } from 'shared/utils/parse-date-time';

import { MetaInteraction } from '../meta-interaction/meta-interaction';

export type MetaStartTimeProps = {
  timeFormat: TimeFormatOptions;
  value?: number | null;
  onChange?: (time?: number) => void;
};

export const MetaStartTime: React.FC<MetaStartTimeProps> = ({
  timeFormat,
  value,
  onChange,
}) => {
  const { t } = useTranslation();
  const buttonRef = useRef<HTMLButtonElement>(null);
  const { menuOpen, openMenu, closeMenu } = useOpenMenu();

  const parsedValue = useMemo(
    () => (isNumber(value) ? parseDateTime(value!, timeFormat) : undefined),
    [timeFormat, value],
  );

  const _onChange = (time?: number) => {
    onChange?.(time);
    closeMenu();
  };

  const onClear = () => {
    onChange?.();
    closeMenu();
  };

  return (
    <>
      <MetaInteraction
        ref={buttonRef}
        icon={ClockWatch}
        onClick={openMenu}
        placeholder={t('dialogs.task-detail.meta.start-time.placeholder')}
        label={parsedValue}
        onClear={parsedValue ? onClear : undefined}
      />
      {menuOpen && (
        <TimePickerMenu
          referenceElement={buttonRef}
          timeFormat={timeFormat}
          value={value}
          onChange={_onChange}
          onCancel={closeMenu}
          optionIcon={ClockWatch}
          placeholder={t(
            `forms.task.fields.startTime.${timeFormat === TimeFormatOptions.Military ? 'military' : 'standard'}.placeholder`,
          )}
          clearLabel={t('task.timeslot.start-time.options.clear.label')}
          hasCustomInput
          canClear
        />
      )}
    </>
  );
};
